import decodeHTMLEntities from '@fogia/core-web/utils/decodeHTMLEntities';
import { productBreadcrumbs as getProductBreadcrumbs } from '@fogia/core-web/utils/storm';
import { APP_ORGANIZATION } from 'config/constants';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const ProductStructuredData = ({
    brand,
    categories,
    description,
    ean,
    images,
    mpn,
    name,
    sku,
    url,
    variants,
    productAvailability,
    averageScore,
    totalReviews,
    isUsedProduct = false,
    statusId,
    color,
    material,
}) => {
    const storeAvailability = useSelector(
        ({ page }) => page.data?.product?.storeAvailability || page.data?.storeAvailability || [],
    );
    const appOrganization = APP_ORGANIZATION;

    const productAvailabilityValue = statusId === 1 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';

    const siteConfig = useSelector(({ application }) => application.site);

    const { protocol, applicationId, languages } = siteConfig || {};
    const origin =
        (languages || []).find((language) => language.id === applicationId)?.uri?.replace(/^\/+|\/+$/g, '') || '';

    const { t } = useTranslation();

    const schema = {
        '@context': 'http://schema.org',
        '@type': 'Product',
        name,
        image: images,
        description,
        sku,
        productID: ean,
        gtin13: ean,
        mpn,
        brand: {
            '@type': 'Brand',
            name: brand,
        },
        offers: {
            '@type': 'Offer',
            url,
            color,
            material,
            itemCondition: isUsedProduct ? 'http://schema.org/UsedCondition' : 'http://schema.org/NewCondition',
            availability: productAvailabilityValue,

            seller: {
                '@type': 'Organization',
                name: appOrganization,
            },
        },
    };

    if (productAvailability?.nextDelivery) {
        schema.offers.availabilityStarts = productAvailability.nextDelivery;
    }

    if (variants.length) {
        const productVariations = variants.map((variation) => {
            const variationName = variation.variant_name || name;
            let variationImage =
                (variation.format || {}).image || (variation.files?.[0] && variation.files[0].format.image);
            const variationMpn = variation.manufacturer?.partNo || variation.manufacturer?.part_no || null;
            const variationSku = variation.partNo || variation.part_no || null;
            const variationEan = variation.eanCode || variation.ean_code || null;

            const availableAtOrFrom = storeAvailability
                .filter((item) => item.partNo === variationSku)[0]
                ?.stores?.map((store) => {
                    const address =
                        store.address?.address && store.address?.postalAddress
                            ? `${store.address?.address}, ${store.address?.postalAddress}`
                            : null;

                    return {
                        '@type': 'Store',
                        name: store.name,
                        branchCode: store.GLN,
                        availability: store.availability,
                        address,
                    };
                });

            variationImage = variationImage
                ? variationImage.endsWith('.jpg')
                    ? variationImage
                    : `${variationImage}.jpg`
                : null;

            return {
                '@context': 'http://schema.org',
                '@type': 'Product',
                name: variationName,
                image: variationImage,
                description,
                sku: variationSku,
                productID: variationEan,
                gtin13: variationEan,
                mpn: variationMpn,
                brand: {
                    '@type': 'Brand',
                    name: brand,
                },
                offers: {
                    '@type': 'Offer',
                    url: `${url}?p=${variation.part_no ?? variation.partNo}`,
                    color,
                    material,
                    itemCondition: isUsedProduct ? 'http://schema.org/UsedCondition' : 'http://schema.org/NewCondition',
                    availability: productAvailabilityValue,
                    seller: {
                        '@type': 'Organization',
                        name: appOrganization,
                    },
                    availableAtOrFrom,
                },
                // TODO: Reviews are not avalible at the moment, add in later stage
                // aggregateRating: null,
                // review: null,
            };
        });

        schema.hasVariant = productVariations;
    }

    if (averageScore && totalReviews) {
        schema.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: averageScore.toString() || null,
            reviewCount: totalReviews.toString() || null,
        };
    }

    const breadcrumbs = [
        {
            '@type': 'ListItem',
            position: 1,
            name: t('home'),
            item: protocol + origin,
        },
    ];

    const productBreadcrumbs = getProductBreadcrumbs(categories);

    if (productBreadcrumbs) {
        productBreadcrumbs.forEach((item, index) => {
            breadcrumbs.push({
                '@type': 'ListItem',
                position: index + 2,
                name: decodeHTMLEntities(item.title),
                item: protocol + origin + item.permalink,
            });
        });
    }

    breadcrumbs.push({
        '@type': 'ListItem',
        position: breadcrumbs.length + 1,
        name: decodeHTMLEntities(name),
    });

    const breadcrumbList = {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs,
    };

    return (
        <Helmet>
            <script type="application/ld+json">{JSON.stringify(schema, null, 2)}</script>
            {!!breadcrumbs.length && (
                <script type="application/ld+json">{JSON.stringify(breadcrumbList, null, 0)}</script>
            )}
        </Helmet>
    );
};

ProductStructuredData.propTypes = {
    averageScore: PropTypes.number,
    brand: PropTypes.string.isRequired,
    categories: PropTypes.array.isRequired,
    description: PropTypes.string.isRequired,
    ean: PropTypes.string.isRequired,
    images: PropTypes.array.isRequired,
    mpn: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    productAvailability: PropTypes.shape({
        inStock: PropTypes.bool,
        preOrder: PropTypes.bool,
        nextDelivery: PropTypes.string,
    }).isRequired,
    sku: PropTypes.string.isRequired,
    totalReviews: PropTypes.number,
    url: PropTypes.string.isRequired,
    variants: PropTypes.array.isRequired,
};

ProductStructuredData.defaultProps = {
    averageScore: null,
    totalReviews: null,
};

export default ProductStructuredData;
